import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../Components/AuthContext';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import MainLayout from '../../Components/Layout/MainLayout/MainLayout';
import { Button } from '../../Components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../../Components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetBody, SheetFooter } from '../../Components/ui/sheet';

const API = process.env.REACT_APP_API_URL;

const inputClass = "h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";
const selectClass = inputClass;

const DURATIONS = [30, 45, 60, 75, 90];
const DOW = [
  { label: 'Sun', value: 0 }, { label: 'Mon', value: 1 }, { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 }, { label: 'Thu', value: 4 }, { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
];

const EMPTY_FORM = {
  clientId: '', clientPackageId: '', locationId: '',
  date: '', startTime: '',
  durationMins: 30, maxClients: 1, notes: '',
  isRecurring: false, daysOfWeek: [], endDate: '',
};

function toCalendarEvents(bookings) {
  return bookings.map(b => {
    const active = (b.clientBookings ?? []).filter(cb => cb.status !== 'cancelled');
    const isFull = active.length >= b.maxClients;
    const isAvailableDuo = b.maxClients > 1 && !isFull;
    const isCompleted = b.status === 'completed';
    const title = active.length > 0 ? active.map(cb => cb.clientName).join(', ') : '(empty)';
    const endMs = new Date(b.scheduledAt).getTime() + b.durationMins * 60000;

    let backgroundColor, borderColor, textColor;
    if (isCompleted) {
      backgroundColor = '#e2e8f0';
      borderColor = '#94a3b8';
      textColor = '#64748b';
    } else if (isAvailableDuo) {
      backgroundColor = 'rgba(34,197,94,0.08)';
      borderColor = '#22c55e';
      textColor = '#15803d';
    } else {
      backgroundColor = '#3b82f6';
      borderColor = '#2563eb';
      textColor = '#ffffff';
    }

    return {
      id: b.bookingId,
      title,
      start: b.scheduledAt,
      end: new Date(endMs).toISOString(),
      backgroundColor,
      borderColor,
      textColor,
      extendedProps: { booking: b },
    };
  });
}

const Schedule = () => {
  const { tenantDomain } = useParams();
  const { updateTenantDomain } = useAuth();
  const calendarRef = useRef(null);

  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [allAvailability, setAllAvailability] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('all');
  const [currentRange, setCurrentRange] = useState(null);
  const [error, setError] = useState(null);

  // Create booking sheet
  const [sheetOpen, setSheetOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [clientPackages, setClientPackages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  // Booking detail dialog
  const [detailBooking, setDetailBooking] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [completing, setCompleting] = useState(false);

  // Cancel recurrence
  const [cancellingRecurrence, setCancellingRecurrence] = useState(false);

  // Add client to existing booking
  const [addClientId, setAddClientId] = useState('');
  const [addClientPackageId, setAddClientPackageId] = useState('');
  const [addClientPackages, setAddClientPackages] = useState([]);
  const [addingClient, setAddingClient] = useState(false);

  useEffect(() => {
    updateTenantDomain(tenantDomain);
    fetchInitialData();
  }, [tenantDomain]);

  const fetchInitialData = async () => {
    try {
      const [clientsRes, locationsRes, availRes] = await Promise.all([
        fetch(`${API}/${tenantDomain}/client`, { credentials: 'include' }),
        fetch(`${API}/${tenantDomain}/location`, { credentials: 'include' }),
        fetch(`${API}/${tenantDomain}/availability`, { credentials: 'include' }),
      ]);
      if (clientsRes.ok) setClients(await clientsRes.json());
      if (locationsRes.ok) {
        const locs = await locationsRes.json();
        setLocations(locs.filter(l => l.isActive));
      }
      if (availRes.ok) setAllAvailability(await availRes.json());
    } catch {
      setError('Failed to load schedule data.');
    }
  };

  const fetchBookings = useCallback(async (start, end) => {
    try {
      const response = await fetch(
        `${API}/${tenantDomain}/booking?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error();
      const data = await response.json();
      setCalendarEvents(toCalendarEvents(data));
    } catch {
      setError('Failed to load bookings.');
    }
  }, [tenantDomain]);

  const handleDatesSet = useCallback((dateInfo) => {
    setCurrentRange({ start: dateInfo.startStr, end: dateInfo.endStr });
    fetchBookings(dateInfo.startStr, dateInfo.endStr);
  }, [fetchBookings]);

  const businessHours = useMemo(() => {
    const windows = selectedLocationId === 'all'
      ? allAvailability
      : allAvailability.filter(w => w.locationId === selectedLocationId);
    return windows.map(w => ({
      daysOfWeek: [w.dayOfWeek],
      startTime: w.startTime,
      endTime: w.endTime,
    }));
  }, [allAvailability, selectedLocationId]);

  const filteredEvents = useMemo(() => {
    if (selectedLocationId === 'all') return calendarEvents;
    return calendarEvents.filter(e => e.extendedProps.booking.locationId === selectedLocationId);
  }, [calendarEvents, selectedLocationId]);

  // ── Create booking ──────────────────────────────────────────────────────

  const handleSelect = (selectInfo) => {
    const start = selectInfo.start;
    const datePart = selectInfo.startStr.split('T')[0];
    const timePart = selectInfo.startStr.split('T')[1]?.substring(0, 5) ?? '09:00';
    const clickedDay = start.getDay();
    setForm({
      ...EMPTY_FORM,
      date: datePart,
      startTime: timePart,
      daysOfWeek: [clickedDay],
      locationId: selectedLocationId === 'all' ? '' : selectedLocationId,
    });
    setClientPackages([]);
    setFormError(null);
    setSheetOpen(true);
  };

  const handleClientSelect = async (clientId) => {
    setForm(f => ({ ...f, clientId, clientPackageId: '', maxClients: 1 }));
    setClientPackages([]);
    if (!clientId) return;
    try {
      const res = await fetch(`${API}/${tenantDomain}/client/${clientId}/package`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      const active = data.filter(cp => cp.status === 'active');
      setClientPackages(active);
      if (active.length > 0) {
        const first = active[0];
        const isDuo = (first.flags ?? []).includes('duo');
        setForm(f => ({ ...f, clientPackageId: first.clientPackageId, maxClients: isDuo ? 2 : 1 }));
      }
    } catch { /* non-fatal */ }
  };

  const handlePackageSelect = (clientPackageId) => {
    const pkg = clientPackages.find(cp => cp.clientPackageId === clientPackageId);
    const isDuo = pkg ? (pkg.flags ?? []).includes('duo') : false;
    setForm(f => ({ ...f, clientPackageId, maxClients: isDuo ? 2 : 1 }));
  };

  const toggleDay = (day) => {
    setForm(f => ({
      ...f,
      daysOfWeek: f.daysOfWeek.includes(day)
        ? f.daysOfWeek.filter(d => d !== day)
        : [...f.daysOfWeek, day],
    }));
  };

  const handleSave = async () => {
    if (!form.clientId || !form.clientPackageId || !form.locationId || !form.date || !form.startTime) {
      setFormError('Client, package, location, date, and start time are required.');
      return;
    }
    if (form.isRecurring && form.daysOfWeek.length === 0) {
      setFormError('Select at least one day for the recurring schedule.');
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      let response;
      if (form.isRecurring) {
        response = await fetch(`${API}/${tenantDomain}/booking/recurrence`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            locationId: form.locationId,
            clientId: form.clientId,
            clientPackageId: form.clientPackageId,
            startDate: form.date,
            endDate: form.endDate || null,
            daysOfWeek: form.daysOfWeek,
            sessionTime: form.startTime,
            durationMins: form.durationMins,
            maxClients: form.maxClients,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            notes: form.notes || null,
          }),
        });
      } else {
        const scheduledAt = new Date(`${form.date}T${form.startTime}`).toISOString();
        response = await fetch(`${API}/${tenantDomain}/booking`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            locationId: form.locationId,
            clientId: form.clientId,
            clientPackageId: form.clientPackageId,
            scheduledAt,
            durationMins: form.durationMins,
            maxClients: form.maxClients,
            notes: form.notes || null,
          }),
        });
      }

      if (response.status === 409) {
        const text = await response.text();
        if (text === 'InsufficientCredits') setFormError('This client has no credits remaining.');
        else if (text === 'BookingConflict') setFormError('This time slot conflicts with an existing booking.');
        else if (text === 'BookingFull') setFormError('This slot is already at full capacity.');
        else if (text.startsWith('OutsideAvailability')) setFormError('This time is outside your availability for this location.');
        else setFormError('Could not save booking. Please check for conflicts.');
        return;
      }
      if (!response.ok) throw new Error();

      if (currentRange) await fetchBookings(currentRange.start, currentRange.end);
      setSheetOpen(false);
    } catch {
      setFormError('Failed to save booking. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Cancel recurrence ────────────────────────────────────────────────────

  const handleCancelRecurrence = async () => {
    if (!detailBooking?.recurrenceId) return;
    setCancellingRecurrence(true);
    try {
      const response = await fetch(
        `${API}/${tenantDomain}/booking/recurrence/${detailBooking.recurrenceId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!response.ok) throw new Error();
      if (currentRange) await fetchBookings(currentRange.start, currentRange.end);
      setDetailBooking(null);
    } catch {
      setError('Failed to cancel recurring series.');
    } finally {
      setCancellingRecurrence(false);
    }
  };

  // ── Cancel booking ───────────────────────────────────────────────────────

  const handleCancelBooking = async () => {
    if (!detailBooking) return;
    setCancelling(true);
    try {
      const response = await fetch(
        `${API}/${tenantDomain}/booking/${detailBooking.bookingId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!response.ok) throw new Error();
      if (currentRange) await fetchBookings(currentRange.start, currentRange.end);
      setDetailBooking(null);
    } catch {
      setError('Failed to cancel booking.');
    } finally {
      setCancelling(false);
    }
  };

  const handleCompleteBooking = async () => {
    if (!detailBooking) return;
    setCompleting(true);
    try {
      const response = await fetch(
        `${API}/${tenantDomain}/booking/${detailBooking.bookingId}/complete`,
        { method: 'PUT', credentials: 'include' }
      );
      if (!response.ok) throw new Error();
      if (currentRange) await fetchBookings(currentRange.start, currentRange.end);
      setDetailBooking(null);
    } catch {
      setError('Failed to mark booking as complete.');
    } finally {
      setCompleting(false);
    }
  };

  const handleAddClientSelect = async (clientId) => {
    setAddClientId(clientId);
    setAddClientPackageId('');
    setAddClientPackages([]);
    if (!clientId) return;
    try {
      const res = await fetch(`${API}/${tenantDomain}/client/${clientId}/package`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      const active = data.filter(cp => cp.status === 'active');
      setAddClientPackages(active);
      if (active.length > 0) setAddClientPackageId(active[0].clientPackageId);
    } catch { /* non-fatal */ }
  };

  const handleAddClientToBooking = async () => {
    if (!detailBooking || !addClientId || !addClientPackageId) return;
    setAddingClient(true);
    try {
      const response = await fetch(`${API}/${tenantDomain}/booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          locationId: detailBooking.locationId,
          clientId: addClientId,
          clientPackageId: addClientPackageId,
          scheduledAt: detailBooking.scheduledAt,
          durationMins: detailBooking.durationMins,
        }),
      });
      if (response.status === 409) {
        const text = await response.text();
        if (text === 'InsufficientCredits') setError('That client has no credits remaining.');
        else if (text === 'BookingFull') setError('This booking is already full.');
        else setError('Could not add client to booking.');
        return;
      }
      if (!response.ok) throw new Error();
      if (currentRange) await fetchBookings(currentRange.start, currentRange.end);
      setDetailBooking(null);
    } catch {
      setError('Failed to add client to booking.');
    } finally {
      setAddingClient(false);
    }
  };

  const handleEventClick = (clickInfo) => {
    setDetailBooking(clickInfo.event.extendedProps.booking);
    setAddClientId('');
    setAddClientPackageId('');
    setAddClientPackages([]);
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const selectedPackage = clientPackages.find(cp => cp.clientPackageId === form.clientPackageId);

  return (
    <MainLayout fullWidth>
      {/* Location filter */}
      <div className="flex items-center gap-2 px-6 py-3 border-b bg-background">
        <button
          onClick={() => setSelectedLocationId('all')}
          className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
            selectedLocationId === 'all'
              ? 'bg-primary text-primary-foreground border-primary'
              : 'text-muted-foreground border-input hover:border-primary/50'
          }`}
        >
          All locations
        </button>
        {locations.map(loc => (
          <button
            key={loc.locationId}
            onClick={() => setSelectedLocationId(loc.locationId)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
              selectedLocationId === loc.locationId
                ? 'bg-primary text-primary-foreground border-primary'
                : 'text-muted-foreground border-input hover:border-primary/50'
            }`}
          >
            {loc.name}
          </button>
        ))}
      </div>

      {error && (
        <div className="mx-6 mt-3 rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">{error}</div>
      )}

      {/* Calendar */}
      <div className="px-6 py-4 fc-wrapper">
        <FullCalendar
          ref={calendarRef}
          plugins={[timeGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          headerToolbar={{ left: 'prev,next today', center: 'title', right: '' }}
          slotDuration="00:30:00"
          slotLabelInterval="01:00:00"
          slotMinTime="05:00:00"
          slotMaxTime="23:00:00"
          allDaySlot={false}
          nowIndicator={true}
          selectable={true}
          selectMirror={true}
          businessHours={businessHours}
          events={filteredEvents}
          select={handleSelect}
          eventClick={handleEventClick}
          datesSet={handleDatesSet}
          height="auto"
          expandRows={true}
          slotLabelFormat={{ hour: 'numeric', minute: '2-digit', omitZeroMinute: true, meridiem: 'short' }}
          dayHeaderFormat={{ weekday: 'short', month: 'numeric', day: 'numeric', omitCommas: true }}
        />
      </div>

      {/* ── Create / Edit Booking Sheet ── */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>New Booking</SheetTitle>
          </SheetHeader>
          <SheetBody>
            <div className="flex flex-col gap-4">
              {formError && (
                <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">{formError}</div>
              )}

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Client <span className="text-destructive">*</span></label>
                <select value={form.clientId} onChange={e => handleClientSelect(e.target.value)} className={selectClass}>
                  <option value="">Select client...</option>
                  {clients.filter(c => c.isActive).map(c => (
                    <option key={c.clientId} value={c.clientId}>{c.firstName} {c.lastName}</option>
                  ))}
                </select>
              </div>

              {clientPackages.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium">Package <span className="text-destructive">*</span></label>
                  <select value={form.clientPackageId} onChange={e => handlePackageSelect(e.target.value)} className={selectClass}>
                    {clientPackages.map(cp => (
                      <option key={cp.clientPackageId} value={cp.clientPackageId}>
                        {cp.packageName} — {cp.creditsRemaining} credits remaining
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {form.clientId && clientPackages.length === 0 && (
                <p className="text-xs text-destructive">This client has no active packages.</p>
              )}

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Location <span className="text-destructive">*</span></label>
                <select value={form.locationId} onChange={e => setForm(f => ({ ...f, locationId: e.target.value }))} className={selectClass}>
                  <option value="">Select location...</option>
                  {locations.map(l => (
                    <option key={l.locationId} value={l.locationId}>{l.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3">
                <div className="flex flex-col gap-1.5 flex-1">
                  <label className="text-sm font-medium">Date <span className="text-destructive">*</span></label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-col gap-1.5 flex-1">
                  <label className="text-sm font-medium">Start time <span className="text-destructive">*</span></label>
                  <input
                    type="time"
                    value={form.startTime}
                    onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))}
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex flex-col gap-1.5 flex-1">
                  <label className="text-sm font-medium">Duration</label>
                  <select value={form.durationMins} onChange={e => setForm(f => ({ ...f, durationMins: Number(e.target.value) }))} className={selectClass}>
                    {DURATIONS.map(d => (
                      <option key={d} value={d}>{d} min</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5 flex-1">
                  <label className="text-sm font-medium">Max clients</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={form.maxClients}
                    onChange={e => setForm(f => ({ ...f, maxClients: Number(e.target.value) }))}
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  rows={2}
                  placeholder="Optional..."
                />
              </div>

              {/* Recurring toggle */}
              <div className="border-t pt-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isRecurring}
                    onChange={e => setForm(f => ({ ...f, isRecurring: e.target.checked }))}
                    className="h-4 w-4 rounded border-input"
                  />
                  <span className="text-sm font-medium">Recurring booking</span>
                </label>

                {form.isRecurring && (
                  <div className="mt-3 flex flex-col gap-3">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium">Repeat on</label>
                      <div className="flex flex-wrap gap-1.5">
                        {DOW.map(d => (
                          <button
                            key={d.value}
                            type="button"
                            onClick={() => toggleDay(d.value)}
                            className={`w-10 h-10 rounded-full text-xs font-medium border transition-colors ${
                              form.daysOfWeek.includes(d.value)
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'text-muted-foreground border-input hover:border-primary/50'
                            }`}
                          >
                            {d.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium">End date <span className="text-muted-foreground text-xs">(leave blank for 1 year)</span></label>
                      <input
                        type="date"
                        value={form.endDate}
                        onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                        className={inputClass}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Credit info */}
              {selectedPackage && (
                <div className="rounded-md bg-muted/30 border px-3 py-2 text-xs text-muted-foreground">
                  {selectedPackage.creditsRemaining} credit(s) remaining — deducted when each session is marked complete.
                </div>
              )}
            </div>
          </SheetBody>
          <SheetFooter>
            <Button variant="outline" onClick={() => setSheetOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !form.clientPackageId}>
              {saving ? 'Saving...' : form.isRecurring ? 'Create Recurring' : 'Create Booking'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* ── Booking Detail Dialog ── */}
      <Dialog open={!!detailBooking} onOpenChange={(open) => { if (!open) setDetailBooking(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {detailBooking?.clientBookings?.filter(cb => cb.status !== 'cancelled').map(cb => cb.clientName).join(', ') || 'Booking'}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            {detailBooking && (
              <div className="flex flex-col gap-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <span className={`font-medium capitalize ${detailBooking.status === 'completed' ? 'text-muted-foreground' : ''}`}>
                    {detailBooking.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Time</span>
                  <span className="font-medium">
                    {new Date(detailBooking.scheduledAt).toLocaleString([], {
                      weekday: 'short', month: 'short', day: 'numeric',
                      hour: 'numeric', minute: '2-digit',
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="font-medium">{detailBooking.durationMins} min</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Location</span>
                  <span className="font-medium">{detailBooking.locationName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Clients</span>
                  <span className="font-medium">
                    {detailBooking.clientBookings?.filter(cb => cb.status !== 'cancelled').length ?? 0} / {detailBooking.maxClients}
                  </span>
                </div>
                {detailBooking.recurrenceId && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Type</span>
                    <span className="font-medium">Recurring</span>
                  </div>
                )}
                {detailBooking.notes && (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-muted-foreground">Notes</span>
                    <span className="italic">{detailBooking.notes}</span>
                  </div>
                )}

                {/* Add client section — only when booking has open spots */}
                {(() => {
                  const activeCount = detailBooking.clientBookings?.filter(cb => cb.status !== 'cancelled').length ?? 0;
                  const hasCapacity = activeCount < detailBooking.maxClients;
                  if (!hasCapacity || detailBooking.status === 'completed' || detailBooking.status === 'cancelled') return null;
                  return (
                    <div className="border-t pt-3 flex flex-col gap-2">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Add client</span>
                      <select
                        value={addClientId}
                        onChange={e => handleAddClientSelect(e.target.value)}
                        className={selectClass}
                      >
                        <option value="">Select client...</option>
                        {clients.filter(c => c.isActive).map(c => (
                          <option key={c.clientId} value={c.clientId}>{c.firstName} {c.lastName}</option>
                        ))}
                      </select>
                      {addClientPackages.length > 0 && (
                        <select
                          value={addClientPackageId}
                          onChange={e => setAddClientPackageId(e.target.value)}
                          className={selectClass}
                        >
                          {addClientPackages.map(cp => (
                            <option key={cp.clientPackageId} value={cp.clientPackageId}>
                              {cp.packageName} — {cp.creditsRemaining} credits
                            </option>
                          ))}
                        </select>
                      )}
                      {addClientId && addClientPackages.length === 0 && (
                        <p className="text-xs text-destructive">This client has no active packages.</p>
                      )}
                      {addClientId && addClientPackageId && (
                        <Button
                          size="sm"
                          onClick={handleAddClientToBooking}
                          disabled={addingClient}
                        >
                          {addingClient ? 'Adding...' : 'Add to booking'}
                        </Button>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailBooking(null)}>Close</Button>
            {detailBooking?.status !== 'completed' && detailBooking?.status !== 'cancelled' && (
              <>
                <Button
                  onClick={handleCompleteBooking}
                  disabled={completing || cancelling || cancellingRecurrence}
                  className="bg-green-600 text-white hover:bg-green-700"
                >
                  {completing ? 'Completing...' : 'Mark Complete'}
                </Button>
                <Button
                  onClick={handleCancelBooking}
                  disabled={cancelling || completing || cancellingRecurrence}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {cancelling ? 'Cancelling...' : 'Cancel Booking'}
                </Button>
                {detailBooking?.recurrenceId && (
                  <Button
                    variant="outline"
                    onClick={handleCancelRecurrence}
                    disabled={cancellingRecurrence || cancelling || completing}
                    className="border-destructive text-destructive hover:bg-destructive/10"
                  >
                    {cancellingRecurrence ? 'Cancelling...' : 'Cancel Series'}
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default Schedule;
